import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
} from '@mui/material';

// Type guard to check if details are CertifierDetails
const isCertifierDetails = (details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails): details is CertifierDetails => {
  return 'yourName' in details || 'title' in details;
};

export const renderExtractedData = (certificate: FullCertificateData | undefined) => {
  if (!certificate?.extractedData) {
    return (
      <Alert severity="warning">
        No extracted data available for this certificate.
      </Alert>
    );
  }

  const { extractedData } = certificate;

  // Helper function to render party details
  const renderPartyDetails = (title: string, details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails | undefined, isCertifier = false) => {
    if (!details) return null;
    
    const hasData = Object.values(details).some(value => value);
    if (!hasData) return null;
    
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            {title}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 1 }}>
            {isCertifier && isCertifierDetails(details) && details.yourName && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Your Name:</Typography>
                <Typography variant="body2">{details.yourName}</Typography>
              </>
            )}
            {isCertifier && isCertifierDetails(details) && details.title && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Title:</Typography>
                <Typography variant="body2">{details.title}</Typography>
              </>
            )}
            {details.companyName && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Company Name:</Typography>
                <Typography variant="body2">{details.companyName}</Typography>
              </>
            )}
            {details.address && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Address:</Typography>
                <Typography variant="body2">{details.address}</Typography>
              </>
            )}
            {details.countryAndPostalCode && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Country & Postal Code:</Typography>
                <Typography variant="body2">{details.countryAndPostalCode}</Typography>
              </>
            )}
            {details.phone && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Phone:</Typography>
                <Typography variant="body2">{details.phone}</Typography>
              </>
            )}
            {details.email && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Email:</Typography>
                <Typography variant="body2">{details.email}</Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Certifier Information */}
      {extractedData.certifier && (
        <Card sx={{ bgcolor: "primary.50" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Certifier Information
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Certifier Type:</strong>{' '}
              <Chip 
                label={extractedData.certifier} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            </Typography>
            {extractedData.certifierDetails && (
              <Box sx={{ mt: 2 }}>
                {renderPartyDetails("Certifier Details", extractedData.certifierDetails, true)}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Exporter Details */}
      {renderPartyDetails("Exporter Details", extractedData.exporterDetails)}

      {/* Producer Details */}
      {renderPartyDetails("Producer Details", extractedData.producerDetails)}

      {/* Importer Details */}
      {renderPartyDetails("Importer Details", extractedData.importerDetails)}

      {/* Items */}
      {extractedData.items && extractedData.items.filter(item => item.INCLUDED).length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Items ({extractedData.items.filter(item => item.INCLUDED).length} included of {extractedData.items.length} total)
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {extractedData.items.filter(item => item.INCLUDED).map((item, index) => (
                <Box key={index}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Item {index + 1}
                    </Typography>
                    {item.INCLUDED && (
                      <Chip 
                        label="INCLUDED" 
                        size="small" 
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 1, ml: 2 }}>
                    {item.partNumber && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Part Number:</Typography>
                        <Typography variant="body2">{item.partNumber}</Typography>
                      </>
                    )}
                    {item.description && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Description:</Typography>
                        <Typography variant="body2">{item.description}</Typography>
                      </>
                    )}
                    {item.origin && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Origin:</Typography>
                        <Box>
                          <Chip 
                            label={item.origin} 
                            size="small" 
                            sx={{
                              backgroundColor: "success.light",
                              color: "success.dark",
                              fontWeight: 600,
                              "& .MuiChip-label": {
                                px: 1.5,
                              },
                            }}
                          />
                        </Box>
                      </>
                    )}
                    {item.hts && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>HTS Code:</Typography>
                        <Typography variant="body2">{item.hts}</Typography>
                      </>
                    )}
                    {item.originCriterion && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Origin Criterion:</Typography>
                        <Typography variant="body2">{item.originCriterion}</Typography>
                      </>
                    )}
                    {item.invoiceNumber && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Invoice Number:</Typography>
                        <Typography variant="body2">{item.invoiceNumber}</Typography>
                      </>
                    )}
                    {item.order && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Order Qty:</Typography>
                        <Typography variant="body2">{item.order}</Typography>
                      </>
                    )}
                    {item.ship && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Ship Qty:</Typography>
                        <Typography variant="body2">{item.ship}</Typography>
                      </>
                    )}
                    {item.net && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Net Weight:</Typography>
                        <Typography variant="body2">{item.net}</Typography>
                      </>
                    )}
                    {item.amount && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Amount:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                          {item.amount}
                        </Typography>
                      </>
                    )}
                    {item.qtyPerPallet && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Qty per Pallet:</Typography>
                        <Typography variant="body2">{item.qtyPerPallet}</Typography>
                      </>
                    )}
                  </Box>
                  {index < (extractedData.items?.filter(item => item.INCLUDED).length || 0) - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};